
import React, { useState, useEffect } from 'react';
import { Box, styled, List, ListItem, ListItemText, Typography } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useNavigate, useLocation } from 'react-router-dom';
import Location from '../Components/Location';
import Company from '../Components/Company';
import ARAP from '../Components/ARAP';
import Property from '../Components/Property';
import Operations from '../Components/Operations';
import Account from '../Components/Account';
import Inventory from '../Components/Inventory';
import Ticketing from '../Components/Ticketing';
import OtherMaster from '../Components/OtherMaster';
import ControlSecurity from '../Components/ControlSecurity';
import AssetStore from '../Components/AssetStore';
import { useSelector } from 'react-redux';
import { selectPermissions } from '../redux/permissionsSlice';
import GstCode from './GstCode';
import TiffinFood from '../Components/TiffinFood';

const StyledTabs = styled(Tabs)({
  backgroundColor: '#e1e7ef',
  borderRadius: '10px',
  padding: '4px',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  minHeight: '36px',
  padding: '8px 16px',
  borderRadius: '5px',
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: '#fff',
    backgroundColor: '#635bff'
  },
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  borderBottom: '1px solid #e1e7ef',
  padding: '10px 15px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#5BB4D2'
  },
}));

const Masters = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = useSelector(selectPermissions);

  const hasModulePermission = (moduleName) => {
    const modulePermission = permissions.find(permission => permission.ModuleName === moduleName);
    if (!modulePermission) return false;

    return Object.entries(modulePermission).some(([key, value]) =>
      ['AddRec', 'EditRec', 'DeleteRec', 'ViewRec'].includes(key) && value === "1"
    );
  }
  const allTabs = [
    {
      label: 'Company',
      path: '/masters/company',
      component: Company,
      moduleName: 'Company'
    },
    {
      label: 'Property',
      path: '/masters/property',
      component: Property,
      moduleName: 'Property'
    },
    {
      label: 'Ar/Ap',
      path: '/masters/arap',
      component: ARAP,
      moduleName: 'Ar/Ap'
    },
    {
      label: 'Location',
      path: '/masters/location',
      component: Location,
      moduleName: 'Location'
    },
    {
      label: 'Accounts',
      path: '/masters/accounts',
      component: Account,
      moduleName: 'Accounts'
    },
    {
      label: 'Ticketing',
      path: '/masters/ticketing',
      component: Ticketing,
      moduleName: 'Ticketing'
    },
    {
      label: 'Assets/Store',
      path: '/masters/AssetStore',
      component: AssetStore,
      moduleName: 'Assets/Store'
    },
    {
      label: 'Operations',
      path: '/masters/operations',
      component: Operations,
      moduleName: 'Operations'
    },
    {
      label: 'Inventory',
      path: '/masters/inventory',
      component: Inventory,
      moduleName: 'Inventory'
    },
    {
      label: 'Other Master',
      path: '/masters/other-master',
      component: OtherMaster,
      moduleName: 'Other Master'
    },
    {
      label: 'Food & Transport',
      path: '/masters/tiffin',
      component: TiffinFood,
      moduleName: 'Food & Transport'
    },
    {
      label: 'Control & Security',
      path: '/masters/control-security',
      component: ControlSecurity,
      moduleName: 'Control & Security'
    },
    {
      label: 'GST/SAC Code',
      path: '/masters/gst-code',
      component: GstCode,
      moduleName: 'GST/SAC Code',
    },

  ];

  // Filter tabs based on permissions
  const tabs = allTabs.filter(tab => hasModulePermission(tab.moduleName));
  const [activeTab, setActiveTab] = useState(0);

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
    navigate(tabs[newValue].path, { state: { permissions } });
  };

  useEffect(() => {
    if (location.pathname === '/masters' && tabs.length > 0) {
      navigate(tabs[0].path, { state: { permissions } }, { replace: true });
      setActiveTab(0);
      return;
    }

    const currentTabIndex = tabs.findIndex((tab) => tab.path === location.pathname);
    if (currentTabIndex !== -1) {
      setActiveTab(currentTabIndex);
    } else if (tabs.length > 0) {
      navigate(tabs[0].path, { state: { permissions } }, { replace: true });
      setActiveTab(0);
    }
  }, [location.pathname, tabs.length, permissions]);



  // If no tabs are available due to permissions, show a message
  if (tabs.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">
          No master sections are available based on your permissions.
        </Typography>
      </Box>
    );
  }

  const ActiveComponent = tabs[activeTab]?.component || tabs[0]?.component;

  return (
    <Box sx={{
      width: '100%',
      p: 2,
      '& .MuiTabs-root': {
        padding: '7px 4px 0px 4px !important'
      }
    }}>
      <Box sx={{ mb: 4 }}>
        <StyledTabs
          value={activeTab}
          onChange={handleChange}
          sx={{ display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' } }}
        >
          {tabs.map((tab, index) => (
            <StyledTab key={index} label={tab.label} />
          ))}
        </StyledTabs>

        <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 2 }}>
          <List>
            {tabs.map((tab, index) => (
              <StyledListItem
                button
                key={index}
                onClick={() => {
                  navigate(tab.path, { state: { permissions } });
                }}
              >
                <ListItemText primary={tab.label} />
              </StyledListItem>
            ))}
          </List>
        </Box>
      </Box>

      <Box>
        {ActiveComponent &&
          <ActiveComponent permissions={permissions} />
        }
      </Box>
    </Box>
  );
};

export default Masters;